/**
 * IIR open routing (navigation fix):
 *  - author + Draft, or canApprove (CM reviewing submission) → editor page
 *  - otherwise → C2 view modal
 *
 * Pass `returnTo` (or rely on the current URL) so Editor Back / viewer Back restore
 * the prior UI state (modals, filters, inputs).
 */

import {hasAnyRole, sanitizeReturnTo} from '../collectionTaskPayload';
import openCollectionMaterial from './openCollectionMaterial';

export function iirReportId(iir) {
    if (!iir) return null;
    return iir.reportId || iir.id || null;
}

export function iirOwnerUserId(iir) {
    if (!iir) return null;
    return iir.ownerUserId || iir.authorUserId || null;
}

export function iirWorkflowStatus(iir) {
    if (!iir) return 'draft';
    return iir.workflowStatus || iir.status || 'draft';
}

export function isIirDraft(iir) {
    return String(iirWorkflowStatus(iir)).toLowerCase() === 'draft';
}

/**
 * Open the editor (not the read-only viewer) when the caller should act on the IIR:
 * author of a draft, or a collection manager reviewing a submitted IIR for approval.
 */
export function shouldOpenIirEditor({iir, userId} = {}) {
    if (!iir || !userId) return false;
    if (Boolean(iir.canApprove)) return true;
    return isIirDraft(iir) && iirOwnerUserId(iir) === userId;
}

/** View-mode Edit affordance is admin-only. */
export function canShowIirViewEditButton({role, roles} = {}) {
    return hasAnyRole(role, roles, ['admin']);
}

function resolveReturnTo({returnTo, location} = {}) {
    const safe = sanitizeReturnTo(returnTo);
    if (safe) return safe;
    if (!location) return null;
    return `${location.pathname || ''}${location.search || ''}${location.hash || ''}` || null;
}

/**
 * Open an IIR in editor (author+draft) or C2 viewer (everyone else).
 *
 * @param {Object} args
 * @param {Object} args.iir  Summary with id/reportId, ownerUserId, workflowStatus
 * @param {string} [args.userId]  Current user uid
 * @param {string} [args.returnTo]
 * @param {Function} args.navigate
 * @param {Object} [args.location]
 */
export default function openCollectionIir({
    iir,
    userId,
    returnTo,
    navigate,
    location,
} = {}) {
    const reportId = iirReportId(iir);
    if (!reportId) {
        console.warn('[openCollectionIir] called without report id');
        return;
    }

    const effectiveReturnTo = resolveReturnTo({returnTo, location});

    if (shouldOpenIirEditor({iir, userId}) && navigate) {
        navigate(`/collection/iir/${encodeURIComponent(reportId)}/edit`, {
            state: effectiveReturnTo ? {returnTo: effectiveReturnTo} : undefined,
        });
        return;
    }

    openCollectionMaterial({
        materialId: reportId,
        materialKind: 'iir',
        returnTo: effectiveReturnTo,
        navigate,
        location,
    });
}

/** Navigate to the IIR editor, preserving returnTo for Back. */
export function navigateToIirEditor({
    reportId,
    navigate,
    location,
    returnTo,
} = {}) {
    if (!reportId || !navigate) return;
    const effectiveReturnTo = resolveReturnTo({returnTo, location});
    navigate(`/collection/iir/${encodeURIComponent(reportId)}/edit`, {
        state: effectiveReturnTo ? {returnTo: effectiveReturnTo} : undefined,
    });
}
